/**
 * Firma / cihaz kaydının oluşturulma bilgisi (#343): rapor tarih seçicisinin
 * alt sınırı ve detay başlığındaki "Oluşturulma · Oluşturan" satırı.
 * Sözleşme: BayinerIOT-Workspace-/docs/api-contracts/detail-consumption-report.md
 */

/** Oluşturulma günü 00:00 (yerel) — öncesi seçilemez. Bilinmiyorsa undefined. */
export const creationMinDate = (createdInfo) => {
  if (!createdInfo) return undefined;
  const d = new Date(createdInfo);
  if (Number.isNaN(d.getTime())) return undefined;
  d.setHours(0, 0, 0, 0);
  return d;
};

const idOf = (ref) => (ref && typeof ref === "object" ? ref._id : ref);

/**
 * Ağaçtaki ebeveyn (#215 kuralı): ana firma → yoksa müşteride bayi.
 * Liste yanıtında `mainFirm` / `dealerFirm` populate edilmiş ya da düz id olabilir.
 */
const parentIdOf = (firm) => {
  const main = idOf(firm?.mainFirm);
  if (main) return String(main);
  const dealers = Array.isArray(firm?.dealerFirm) ? firm.dealerFirm : [];
  const dealer = firm?.firmType === "customer" ? idOf(dealers[0]) : null;
  return dealer ? String(dealer) : null;
};

/**
 * Firmanın ağındaki (kendisi + tüm alt firmaları / bayinin müşterileri + üst
 * zinciri) en eski oluşturulma günü (#270). Rapor alt firmaları da kapsadığı
 * için tek firmanın tarihi alt sınır olamaz. Listede olmayan firma atlanır.
 *
 * @param {Object} firm  rapor alınan firma (`createdInfo` taşır)
 * @param {Object[]} firms panel firma listesi (`mainFirm`, `dealerFirm`, `createdInfo`)
 */
export const networkMinDate = (firm, firms = []) => {
  const rootId = firm?._id ? String(firm._id) : null;
  const byId = new Map();
  const children = new Map();
  (firms || []).forEach((f) => {
    if (!f?._id) return;
    const id = String(f._id);
    byId.set(id, f);
    const parentId = parentIdOf(f);
    if (parentId && parentId !== id) {
      if (!children.has(parentId)) children.set(parentId, []);
      children.get(parentId).push(id);
    }
  });

  const related = new Set();
  if (rootId) {
    // Alt ağaç
    const queue = [rootId];
    while (queue.length > 0) {
      const id = queue.shift();
      if (related.has(id)) continue;
      related.add(id);
      queue.push(...(children.get(id) || []));
    }
    // Üst zincir
    let parentId = parentIdOf(byId.get(rootId) || firm);
    while (parentId && !related.has(parentId)) {
      related.add(parentId);
      parentId = parentIdOf(byId.get(parentId));
    }
  }

  const days = [creationMinDate(firm?.createdInfo)];
  related.forEach((id) => days.push(creationMinDate(byId.get(id)?.createdInfo)));
  const valid = days.filter(Boolean);
  return valid.length > 0 ? new Date(Math.min(...valid.map((d) => d.getTime()))) : undefined;
};

/** Tarih aralığının başlangıcı oluşturulmadan önceyse oluşturulma gününe çekilir. */
export const clampToCreation = (date, createdInfo) => {
  const min = creationMinDate(createdInfo);
  return min && date && new Date(date) < min ? min : date;
};

/** "Oluşturulma: 12 Mart 2024 · Oluşturan: Ad Soyad" — bilinmeyen kısım "—". */
export const creationLine = ({ createdInfo, createdByName } = {}) => {
  const d = createdInfo ? new Date(createdInfo) : null;
  const date =
    d && !Number.isNaN(d.getTime())
      ? d.toLocaleDateString("tr-TR", { day: "numeric", month: "long", year: "numeric" })
      : "—";
  return `Oluşturulma: ${date} · Oluşturan: ${createdByName || "—"}`;
};
